import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Grid, 
  Typography, 
  IconButton, 
  Button,
  Paper, 
  Alert, 
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { StatsAtGlance } from 'components/emailHistory/StatsAtGlance';
import { AIInsight } from 'components/emailHistory/AIInsight';
import { QuickFilters } from 'components/emailHistory/QuickFilters';
import { StatChip } from 'components/emailHistory/StatChip';
import { ExpandedRowCampaign } from 'components/emailHistory/ExpandedRowCampaign';
import { EmailCard } from 'components/emailHistory/EmailCard';
import { SegmentedBarChart } from 'components/emailHistory/charts';
import { useEmailHistoryFilters } from 'hooks/emailHistory/useEmailHistoryFilters';
import { useCampaigns } from 'hooks/emailHistory/useCampaigns';
import { useIsMobile } from 'hooks/useIsMobile';
import { DEBOUNCE_DELAY, CAMPAIGN_SORT_FIELDS } from 'constants/emailHistory';
import { ROUTE_MAIL_LIST } from 'constants/routerPath';

/**
 * Campaign row component with inline expansion
 */
function CampaignRow({ campaign, expandedRowIds, setExpandedRowIds, onNavigateToBulkEmails }) {
  const isExpanded = expandedRowIds.includes(campaign.campaignId);

  const toggleExpand = () => {
    if (isExpanded) {
      setExpandedRowIds(expandedRowIds.filter(id => id !== campaign.campaignId));
    } else {
      setExpandedRowIds([...expandedRowIds, campaign.campaignId]);
    }
  };

  return (
    <React.Fragment>
      {/* Main Data Row */}
      <TableRow hover sx={{ cursor: 'pointer' }}>
        <TableCell>
          <IconButton
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            aria-expanded={isExpanded}
            size="small"
            onClick={toggleExpand}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <Typography variant="body1">{campaign.campaignName}</Typography>
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <StatChip value={campaign.totalEmailsSent} />
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <StatChip value={campaign.delivered} />
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <StatChip value={campaign.uniqueOpens} />
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <StatChip value={campaign.uniqueClicks} />
        </TableCell>
        <TableCell>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => onNavigateToBulkEmails(campaign.campaignId)}
            endIcon={<ChevronRightIcon />}
            sx={{ minWidth: 100 }}
          >
            View
          </Button>
        </TableCell>
      </TableRow>

      {/* Expanded Details Row */}
      <TableRow>
        <TableCell sx={{ p: 0, borderBottom: isExpanded ? 1 : 0, bgcolor: 'white' }} colSpan={7}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 3, bgcolor: 'white' }}>
              <ExpandedRowCampaign
                campaignId={campaign.campaignId}
                onNavigateToBulkEmails={onNavigateToBulkEmails}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

/**
 * @component CampaignsTab
 * @description Campaigns tab showing all email campaigns with real data
 * @returns {JSX.Element}
 */
function CampaignsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { filters, setFilter, navigateToTab } = useEmailHistoryFilters();
  const { campaigns, totalRows, stats, isLoading, error } = useCampaigns(filters);
  const [expandedRowIds, setExpandedRowIds] = useState([]);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const isMobile = useIsMobile();

  // Initialize sortBy to 'lastSendDate' if not set or invalid for campaigns
  useEffect(() => {
    if (!filters.sortBy || !CAMPAIGN_SORT_FIELDS.includes(filters.sortBy)) {
      setFilter('sortBy', 'lastSendDate');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Sync searchInput with URL params when they change externally
  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  // Debounced search effect - only update URL if search actually changed
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmedSearch = searchInput ? searchInput.trim() : '';
      const currentSearch = filters.search || '';
      
      // Only update if search actually changed
      if (trimmedSearch !== currentSearch) {
        setSearchParams((prevParams) => {
          const newParams = new URLSearchParams(prevParams);
          if (trimmedSearch) {
            newParams.set('search', trimmedSearch);
          } else {
            newParams.delete('search');
          }
          newParams.set('page', '0');
          return newParams;
        });
      }
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchInput, filters.search, setSearchParams]);

  // Handle search input change
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
  }, []);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setFilter('page', newPage);
    setExpandedRowIds([]); // Close expanded rows when changing pages
  };

  const handleChangeRowsPerPage = (event) => {
    setFilter('pageSize', parseInt(event.target.value, 10));
    setFilter('page', 0);
    setExpandedRowIds([]);
  };

  // Map UI field names to API field names for sorting
  const mapFieldToApiField = (field) => {
    const fieldMap = {
      'delivered': 'deliveryRate',
      'uniqueOpens': 'openRate',
      'uniqueClicks': 'clickRate',
    };
    return fieldMap[field] || field;
  };

  // Handle sorting
  const handleSort = (field) => {
    const apiField = mapFieldToApiField(field);
    const isAsc = filters.sortBy === apiField && filters.sortOrder === 'asc';
    setFilter('sortBy', apiField);
    setFilter('sortOrder', isAsc ? 'desc' : 'asc');
  };

  // Navigate to bulk emails tab with campaign filter
  const handleNavigateToBulkEmails = (campaignId) => {
    navigateToTab('bulk-emails', { campaignId });
  };

  // Navigate to list manager to create new campaign
  const handleCreateNewCampaign = () => {
    navigate(ROUTE_MAIL_LIST);
  };

  // Define sortable columns
  const columns = [
    { field: 'expand', label: '', sortable: false, width: '50px' },
    { field: 'campaignName', label: 'Campaign Name', sortable: true, width: '250px' },
    { field: 'totalEmailsSent', label: 'Sent', sortable: true, width: '90px' },
    { field: 'delivered', label: 'Delivered', sortable: true, width: '90px' },
    { field: 'uniqueOpens', label: 'Opens', sortable: true, width: '85px' },
    { field: 'uniqueClicks', label: 'Clicks', sortable: true, width: '85px' },
    { field: 'actions', label: '', sortable: false, width: '110px' },
  ];

  // Prepare stats data
  const statsData = [
    { label: stats.totalCampaigns === 1 ? 'Campaign' : 'Campaigns', value: stats.totalCampaigns },
    { label: 'Total Bulk Sends', value: stats.totalBulkSends },
    { label: 'Avg Delivery Rate', value: `${stats.avgDeliveryRate}%` },
    { label: 'Avg Open Rate', value: `${stats.avgOpenRate}%` },
  ];

  // Generate dynamic title
  const statsTitle = "Viewing All Campaigns";
  const statsSubtitle = "A Campaign is a collection of related emails delivered to the same group of contacts over time.";

  return (
    <Box>
      {/* Stats + AI Overview Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <StatsAtGlance stats={statsData} loading={isLoading} title={statsTitle} subtitle={statsSubtitle}>
            {/*}
            {campaigns.length > 0 && (
              <SegmentedBarChart
                data={campaigns.map(campaign => ({
                  campaignId: campaign.campaignId,
                  campaignName: campaign.campaignName,
                  value: campaign.totalBulkSends || 0,
                }))}
                loading={isLoading}
              />
            )}
            */}
          </StatsAtGlance>
        </Grid>
        <Grid item xs={12} md={4}>
          <AIInsight tab="campaigns" filters={filters} />
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Quick Filters and Data Table */}
      <Paper>
        <QuickFilters
          searchValue={searchInput}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search campaigns..."
          showDateRange={false}
          startButton={
            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={handleCreateNewCampaign}
              fullWidth={isMobile}
            >
              Create new campaign
            </Button>
          }
        />
        {isMobile ? (
          <Box sx={{ p: 2 }}>
            {isLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : campaigns.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No campaigns yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create a mail list and send your first campaign to see it here.
                </Typography>
              </Box>
            ) : (
              <>
                {campaigns.map((campaign) => {
                  const isExpanded = expandedRowIds.includes(campaign.campaignId);
                  return (
                    <EmailCard
                      key={campaign.campaignId}
                      data={{
                        campaignName: campaign.campaignName,
                        totalEmailsSent: campaign.totalEmailsSent,
                        delivered: campaign.delivered,
                        uniqueOpens: campaign.uniqueOpens,
                        uniqueClicks: campaign.uniqueClicks,
                      }}
                      type="campaign"
                      isExpanded={isExpanded}
                      onToggleExpand={() => {
                        if (isExpanded) {
                          setExpandedRowIds(expandedRowIds.filter(id => id !== campaign.campaignId));
                        } else {
                          setExpandedRowIds([...expandedRowIds, campaign.campaignId]);
                        }
                      }}
                      expandedContent={
                        isExpanded ? (
                          <ExpandedRowCampaign
                            campaignId={campaign.campaignId}
                            onNavigateToBulkEmails={handleNavigateToBulkEmails}
                          />
                        ) : null
                      }
                      actionButton={
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          endIcon={<ChevronRightIcon />}
                          onClick={() => handleNavigateToBulkEmails(campaign.campaignId)}
                        >
                          View
                        </Button>
                      }
                      onActionClick={() => handleNavigateToBulkEmails(campaign.campaignId)}
                    />
                  );
                })}
                <TablePagination
                  component="div"
                  count={totalRows}
                  page={filters.page}
                  onPageChange={handleChangePage}
                  rowsPerPage={filters.pageSize}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[]}
                />
              </>
            )}
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.field}
                        sx={{ width: column.width, fontWeight: 600 }}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={filters.sortBy === mapFieldToApiField(column.field)}
                            direction={filters.sortBy === mapFieldToApiField(column.field) ? filters.sortOrder : 'asc'}
                            onClick={() => handleSort(column.field)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : campaigns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No campaigns yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Create a mail list and send your first campaign to see it here.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    campaigns.map((campaign) => (
                      <CampaignRow
                        key={campaign.campaignId}
                        campaign={campaign}
                        expandedRowIds={expandedRowIds}
                        setExpandedRowIds={setExpandedRowIds}
                        onNavigateToBulkEmails={handleNavigateToBulkEmails}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalRows}
              page={filters.page}
              onPageChange={handleChangePage}
              rowsPerPage={filters.pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}

export default CampaignsTab;

